import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { registrationSignupAt } from 'src/helpers/participantList';
import { formatRegistrationSignupInfo } from 'src/helpers/registrationPrice';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { WizardCheckinDraft } from 'src/helpers/userCheckinWizard';
import { IEdition } from 'src/interfaces/Edition';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IBaseTechnicalType } from 'src/interfaces/TechnicalType';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  activeEdition?: IEdition;
  participantsToCheckIn: IEnrichedParticipant[];
  checkinDrafts: Record<string, WizardCheckinDraft>;
  onUpdateDraft: (participantId: string, patch: Partial<WizardCheckinDraft>) => void;
  competitionsById?: Record<string, IBaseTechnicalType>;
  distancesById?: Record<string, IBaseTechnicalType>;
  registeredCount: number;
  checkedInCount: number;
  onCheckInAll: () => void;
  isCheckingIn: boolean;
  canCheckIn: boolean;
  showCheckInRequiredWarning?: boolean;
}

export default function CheckinStep({
  activeEdition,
  participantsToCheckIn,
  checkinDrafts,
  onUpdateDraft,
  competitionsById,
  distancesById,
  registeredCount,
  checkedInCount,
  onCheckInAll,
  isCheckingIn,
  canCheckIn,
  showCheckInRequiredWarning = false,
}: Props) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const dateFormat = useSystemOptionsStore(s => s.dateFormat);

  const editionCompetitions = activeEdition?.competitions ?? [];
  const groupPrice =
    typeof activeEdition?.groupPrice === 'number' && !Number.isNaN(activeEdition.groupPrice)
      ? activeEdition.groupPrice
      : undefined;

  const handleCompetitionChange = (participantId: string, competitionId: string) => {
    const comp = editionCompetitions.find(c => c.competitionId === competitionId);
    const distanceId = sortDistanceIds(comp?.distances ?? [], distancesById)[0] ?? '';
    onUpdateDraft(participantId, { competitionId, distanceId });
  };

  if (!canCheckIn) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {t('userCheckinWizard.checkInSectionTitle')}
        </Typography>
        <Typography color="text.secondary">{t('userCheckinWizard.noCheckInNeeded')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('userCheckinWizard.checkInSectionTitle')}
      </Typography>
      {showCheckInRequiredWarning ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('userCheckinWizard.checkInRequiredBeforeNext')}
        </Alert>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('userCheckinWizard.checkInSectionHint', {
          registered: registeredCount,
          checkedIn: checkedInCount,
        })}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('form.name')}</TableCell>
              <TableCell>{t('drawer.competitions')}</TableCell>
              <TableCell>{t('drawer.distances')}</TableCell>
              <TableCell>{t('export.price')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participantsToCheckIn.map(p => {
              const draft = checkinDrafts[p.id] ?? {
                competitionId: p.registration?.competitionId ?? '',
                distanceId: p.registration?.distanceId ?? '',
                price: p.registration?.price ?? 0,
              };
              const distancesForComp = sortDistanceIds(
                editionCompetitions.find(c => c.competitionId === draft.competitionId)?.distances ??
                  [],
                distancesById,
              );
              const signupInfo = formatRegistrationSignupInfo(
                t,
                activeEdition,
                registrationSignupAt(p.registration, p.createdAt),
                dateFormat[currentLang],
              );

              return (
                <TableRow key={p.id}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">
                      {p.lastName} {p.firstName}
                    </Typography>
                    {signupInfo ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {signupInfo}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('drawer.competitions')}</InputLabel>
                      <Select
                        value={draft.competitionId}
                        label={t('drawer.competitions')}
                        onChange={e => handleCompetitionChange(p.id, e.target.value)}
                      >
                        {editionCompetitions.map(comp => {
                          const competition = competitionsById?.[comp.competitionId];
                          return competition ? (
                            <MenuItem key={comp.competitionId} value={comp.competitionId}>
                              {competition.i18n[currentLang]}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small" disabled={!draft.competitionId}>
                      <InputLabel>{t('drawer.distances')}</InputLabel>
                      <Select
                        value={draft.distanceId}
                        label={t('drawer.distances')}
                        onChange={e => onUpdateDraft(p.id, { distanceId: e.target.value })}
                      >
                        {distancesForComp.map(distanceId => {
                          const distance = distancesById?.[distanceId];
                          return distance ? (
                            <MenuItem key={distanceId} value={distanceId}>
                              {distance.i18n[currentLang]}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={draft.price}
                        onChange={e =>
                          onUpdateDraft(p.id, { price: Number(e.target.value) || 0 })
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                            ),
                          },
                        }}
                      />
                      {groupPrice != null ? (
                        <Button
                          size="small"
                          onClick={() => onUpdateDraft(p.id, { price: groupPrice })}
                        >
                          {t('checkins.applyGroupPrice')}
                        </Button>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" color="secondary" onClick={onCheckInAll} disabled={isCheckingIn}>
          {t('userCheckinWizard.checkInAll')}
        </Button>
      </Box>
    </Box>
  );
}
